import { recipientCategoryOptions } from './constants';

export type RecipientCategoryOption = (typeof recipientCategoryOptions)[number];

// TODO: Remove once visible_type_filters is backfilled for these clients in staging and prod.
const FALLBACK_VISIBLE_TYPE_FILTERS: Record<number, string[]> = {
  166: ['PAC', 'US_SENATE', 'US_HOUSE', 'US_OTHER'],
  206: ['PAC', 'US_SENATE', 'US_HOUSE', 'US_OTHER'],
};

const hasAllowlist = (
  visibleTypeFilters: string[] | null | undefined
): visibleTypeFilters is string[] =>
  Array.isArray(visibleTypeFilters) && visibleTypeFilters.length > 0;

export const resolveVisibleTypeFilters = (
  clientId: number,
  visibleTypeFilters: string[] | null | undefined
): string[] | null | undefined =>
  hasAllowlist(visibleTypeFilters) ? visibleTypeFilters : FALLBACK_VISIBLE_TYPE_FILTERS[clientId];

// A missing or empty allowlist means the cart doesn't restrict the Type filter.
export const getVisibleTypeOptions = (
  options: RecipientCategoryOption[],
  visibleTypeFilters: string[] | null | undefined
): RecipientCategoryOption[] => {
  if (!hasAllowlist(visibleTypeFilters)) return options;

  const allowed = new Set(visibleTypeFilters);
  return options.flatMap<RecipientCategoryOption>(option => {
    if (!option.children) return allowed.has(option.value) ? [option] : [];
    const children = option.children.filter(child => allowed.has(child.value));
    return children.length > 0 ? [{ ...option, children }] : [];
  });
};

export const getTypeOptionValues = (options: RecipientCategoryOption[]): string[] =>
  options.flatMap(option => [option.value, ...(option.children ?? []).map(child => child.value)]);

const isRestrictedGroup = (option: RecipientCategoryOption): boolean => {
  const fullOption = recipientCategoryOptions.find(candidate => candidate.value === option.value);
  return Boolean(
    option.children && fullOption?.children && option.children.length < fullOption.children.length
  );
};

// The search API reads a lone group value as "the whole group", which would bring back children
// the cart hides, so a restricted group's value only counts alongside one of its children.
export const getAllowedSelectedTypes = (
  selectedTypes: string[],
  visibleTypeOptions: RecipientCategoryOption[]
): string[] => {
  const allowedTypes = new Set(getTypeOptionValues(visibleTypeOptions));
  const lonelyRestrictedGroups = new Set(
    visibleTypeOptions
      .filter(
        option =>
          isRestrictedGroup(option) &&
          !option.children?.some(child => selectedTypes.includes(child.value))
      )
      .map(option => option.value)
  );
  return selectedTypes.filter(type => allowedTypes.has(type) && !lonelyRestrictedGroups.has(type));
};
